export type CredentialState = 'in_vault' | 'not_in_vault' | 'missing' | 'not_needed';
export type TriggerMode = 'scheduled' | 'on_demand';

export type RegistryFacts = {
  trigger_mode?: TriggerMode;
  owner?: {
    app_id?: string | null;
    app_name?: string | null;
    unowned?: boolean;
    unowned_reason?: string | null;
  };
  credential_state?: CredentialState;
  credential_outside_vault?: boolean;
  connected_accounts?: { count?: number; names?: string[] };
  pipeline_count?: number;
  interval_seconds?: number | null;
  last_run?: { at?: string | null; result?: 'success' | 'failed' | null; error?: string | null };
  stalled?: boolean;
  stalled_reason?: string | null;
  apps?: Array<{ app_id?: string | null; app_name?: string | null; feed?: boolean }>;
};

export type RegistryRow = {
  api_id: string;
  api_name?: string;
  provider?: string;
  status?: string;
  app_id?: string;
  created_at?: string | null;
  trigger_mode?: TriggerMode;
  registry?: RegistryFacts;
};

export type RegistryFilters = {
  appId: string;
  provider: string;
  status: string;
  stalledOnly: boolean;
  unownedOnly: boolean;
  search: string;
};

export const EMPTY_REGISTRY_FILTERS: RegistryFilters = {
  appId: 'all',
  provider: 'all',
  status: 'all',
  stalledOnly: false,
  unownedOnly: false,
  search: '',
};

export const UNOWNED_GROUP_LABEL = 'Platform / unowned';
export const ON_DEMAND_LABEL = 'on demand — app-triggered';

export const CREDENTIAL_STATE_LABEL: Record<CredentialState, string> = {
  in_vault: 'In Vault',
  not_in_vault: 'Not in Vault',
  missing: 'Missing',
  not_needed: 'Not needed',
};

export function credentialStateColor(state?: CredentialState): 'success' | 'warning' | 'error' | 'default' {
  if (state === 'in_vault') return 'success';
  if (state === 'not_in_vault') return 'warning';
  if (state === 'missing') return 'error';
  return 'default';
}

export function isOnDemand(row?: { trigger_mode?: TriggerMode; registry?: RegistryFacts } | null): boolean {
  return (row?.registry?.trigger_mode || row?.trigger_mode) === 'on_demand';
}

export function ownerKey(row: RegistryRow): string {
  return row.registry?.owner?.unowned ? 'unowned' : String(row.registry?.owner?.app_id || 'unowned');
}

export function ownerLabel(row: RegistryRow): string {
  if (row.registry?.owner?.unowned) return UNOWNED_GROUP_LABEL;
  return row.registry?.owner?.app_name || row.registry?.owner?.app_id || UNOWNED_GROUP_LABEL;
}

export function formatInterval(row: RegistryRow): string {
  if (isOnDemand(row)) return 'on demand';
  const seconds = row.registry?.interval_seconds;
  if (!seconds) return '—';
  if (seconds % 86400 === 0) return `every ${seconds / 86400}d`;
  if (seconds % 3600 === 0) return `every ${seconds / 3600}h`;
  if (seconds % 60 === 0) return `every ${seconds / 60}m`;
  return `every ${seconds}s`;
}

export function stalledLabel(reason?: string | null): string {
  if (reason === 'pending_authentication_over_24h') return 'Pending auth over 24h';
  if (reason === 'no_success_in_24h') return 'No success in 24h';
  return 'Stalled';
}

export function ownerOptions(rows: RegistryRow[]): Array<{ key: string; label: string }> {
  const seen = new Map<string, string>();
  for (const row of rows) {
    const key = ownerKey(row);
    if (!seen.has(key)) seen.set(key, ownerLabel(row));
  }
  return Array.from(seen.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function distinctValues(rows: RegistryRow[], pick: (row: RegistryRow) => string | undefined): string[] {
  return Array.from(new Set(rows.map(pick).filter((v): v is string => Boolean(v)))).sort();
}

export type ProviderApi = {
  row: RegistryRow;
  apps: Array<{ app_id: string; app_name: string; feed: boolean }>;
  toMerge: boolean;
};

export type ProviderCard = {
  provider: string;
  apis: ProviderApi[];
};

const HIDDEN_STATUSES = new Set(['archived', 'deleted']);

/** Names that differ only by forecast/platform wording are the same product. */
export function productStem(name?: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/\b(api|forecast|weather|platform|maps)\b/g, ' ')
    .replace(/\bv\d+(?:\.\d+)*\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function appsOf(row: RegistryRow): Array<{ app_id: string; app_name: string; feed: boolean }> {
  const listed = row.registry?.apps || [];
  const out: Array<{ app_id: string; app_name: string; feed: boolean }> = [];
  const seen = new Set<string>();
  const add = (id?: string | null, name?: string | null, feed?: boolean) => {
    const appId = String(id || '').trim();
    if (!appId || seen.has(appId)) return;
    seen.add(appId);
    out.push({ app_id: appId, app_name: name || appId, feed: feed !== false });
  };
  for (const app of listed) add(app.app_id, app.app_name, app.feed);
  const shared = row.registry?.owner?.unowned_reason === 'shared' || !row.app_id && row.registry?.owner?.unowned;
  if (!out.length && !shared && row.registry?.owner?.app_id && !row.registry.owner.unowned) {
    add(row.registry.owner.app_id, row.registry.owner.app_name, true);
  }
  return out;
}

export function groupRegistryByProvider(rows: RegistryRow[]): ProviderCard[] {
  const visible = rows.filter((row) => !HIDDEN_STATUSES.has(String(row.status || '').toLowerCase()));
  const groups = new Map<string, RegistryRow[]>();
  for (const row of visible) {
    const key = String(row.provider || 'Unknown provider').trim().toLowerCase() || 'unknown provider';
    const list = groups.get(key) || [];
    list.push(row);
    groups.set(key, list);
  }
  return Array.from(groups.values())
    .map((group) => {
      const counts = new Map<string, number>();
      for (const row of group) {
        const stem = productStem(row.api_name || row.api_id);
        counts.set(stem, (counts.get(stem) || 0) + 1);
      }
      const apis = group
        .slice()
        .sort((a, b) => String(a.api_name || a.api_id).localeCompare(String(b.api_name || b.api_id)))
        .map((row) => ({
          row,
          apps: appsOf(row),
          toMerge: (counts.get(productStem(row.api_name || row.api_id)) || 0) > 1,
        }));
      return { provider: String(group[0].provider || 'Unknown provider').trim() || 'Unknown provider', apis };
    })
    .sort((a, b) => a.provider.localeCompare(b.provider));
}

export function filterRegistryRows(rows: RegistryRow[], filters: RegistryFilters): RegistryRow[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.appId !== 'all' && ownerKey(row) !== filters.appId) return false;
    if (filters.provider !== 'all' && String(row.provider || '') !== filters.provider) return false;
    if (filters.status !== 'all' && String(row.status || '') !== filters.status) return false;
    if (filters.stalledOnly && !row.registry?.stalled) return false;
    if (filters.unownedOnly && !row.registry?.owner?.unowned) return false;
    if (needle) {
      const blob = `${row.api_name || ''} ${row.api_id}`.toLowerCase();
      if (!blob.includes(needle)) return false;
    }
    return true;
  });
}